/**
 * Página de Login
 * Mobile-first: CTAs en thumb zone, touch targets 48px+
 * Sprint 3: Flujo de vinculación de dispositivo
 * Sprint 5: Manejo de cuenta no verificada + link forgot password
 * Sprint 7: Login biométrico (huella digital) en dispositivos móviles
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { APP_HAS_LOGGED_IN_ONCE_KEY, MARKETPLACE_MODE_ONBOARDED_KEY, useAuthStore } from '../../stores';
import { useBiometricStore } from '../../stores/biometricStore';
import { biometricService, getBiometryName } from '../../services/biometric';
import { SocialAuthButton, type SocialProvider } from '../../components/auth/SocialAuthButton';
import { Input, Button, Spinner } from '../../components/ui';
import { DeviceMismatchModal, DeviceAlreadyBoundModal } from '../../components/device';
import { BiometricLoginButton, BiometricPrompt } from '../../components/biometric';
import { savePendingEmail } from '../../services/otp';
import { TOKEN_STORAGE_KEY } from '../../config';
import type { ApiError } from '../../types';
import { normalizeLoginFieldErrors } from './loginErrors';

interface LoginLocationState {
    email?: string;
    from?: {
        pathname?: string;
        search?: string;
    };
    message?: string;
}

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LoginLocationState | null;
    const {
        login,
        completeBind,
        resetLoginState,
        isLoading,
        error,
        clearError,
        loginDeviceState,
        pendingCredentials,
    } = useAuthStore();

    const [email, setEmail] = useState(() => locationState?.email ?? '');
    const [password, setPassword] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [socialAuthMessage, setSocialAuthMessage] = useState<string | null>(null);
    const [hasLoggedInBefore, setHasLoggedInBefore] = useState(
        () => localStorage.getItem(APP_HAS_LOGGED_IN_ONCE_KEY) === '1'
    );

    // Sprint 8: Checkbox para activar biométrico durante login
    const [enableBiometricOnLogin, setEnableBiometricOnLogin] = useState(false);
    const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
    const [isBiometricSetupLoading, setIsBiometricSetupLoading] = useState(false);
    const [biometricSetupPayload, setBiometricSetupPayload] = useState<{
        email: string;
        token: string;
    } | null>(null);

    // Sprint 7: Biometric store
    const {
        isAvailable: biometricAvailable,
        isEnabled: biometricEnabled,
        hasCredentials,
        biometryType,
        initialize: initBiometric,
        enableBiometric,
    } = useBiometricStore();

    // Sprint 7: Flag para saber si biométrico terminó de inicializarse
    const biometricInitialized = useBiometricStore((s) => s.isInitialized);

    // Evita ejecutar la lógica post-login más de una vez por intento de login
    const postLoginHandledRef = useRef(false);

    // Derivar el estado del modal directamente del loginDeviceState
    const showMismatchModal = useMemo(
        () => loginDeviceState === 'device_mismatch',
        [loginDeviceState]
    );

    const showAlreadyBoundModal = useMemo(
        () => loginDeviceState === 'device_already_bound',
        [loginDeviceState]
    );

    // Sprint 5: Detectar cuenta no verificada
    const showAccountNotVerified = useMemo(
        () => loginDeviceState === 'account_not_verified',
        [loginDeviceState]
    );

    const navigateAfterLogin = useCallback(() => {
        const redirectPath = locationState?.from?.pathname;
        const redirectSearch = locationState?.from?.search ?? '';

        if (redirectPath) {
            navigate(`${redirectPath}${redirectSearch}`, { replace: true });
            return;
        }

        const currentUser = useAuthStore.getState().user;
        navigate(currentUser?.is_admin ? '/admin' : '/');
    }, [locationState?.from?.pathname, locationState?.from?.search, navigate]);

    // Navegar cuando el login es exitoso
    // Sprint 8: Con prompt + autenticación biométrica cuando el checkbox está activo
    useEffect(() => {
        if (loginDeviceState !== 'success') {
            postLoginHandledRef.current = false;
            setShowBiometricPrompt(false);
            setBiometricSetupPayload(null);
            return;
        }

        if (!biometricInitialized || postLoginHandledRef.current) {
            return;
        }

        postLoginHandledRef.current = true;

        const currentUser = useAuthStore.getState().user;
        const currentToken = useAuthStore.getState().token;

        if (!currentUser || !currentToken) {
            navigateAfterLogin();
            return;
        }

        const shouldShowBiometricPrompt =
            enableBiometricOnLogin &&
            biometricAvailable &&
            !biometricEnabled &&
            Capacitor.isNativePlatform();

        if (shouldShowBiometricPrompt) {
            setBiometricSetupPayload({
                email: currentUser.email,
                token: currentToken,
            });
            setShowBiometricPrompt(true);
            return;
        }

        navigateAfterLogin();
    }, [
        loginDeviceState,
        biometricInitialized,
        enableBiometricOnLogin,
        biometricAvailable,
        biometricEnabled,
        navigateAfterLogin,
    ]);

    // Sprint 7: Inicializar biométrico al montar
    useEffect(() => {
        initBiometric();
    }, [initBiometric]);

    // Mensaje transitorio desde verify/reset y limpieza de history state para evitar mensajes "colgados"
    useEffect(() => {
        const message = locationState?.message;
        if (!message) {
            return;
        }

        setSuccessMessage(message);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.pathname, locationState?.message, navigate]);

    // Actualizar bandera local cuando ya hubo un login exitoso en la app
    useEffect(() => {
        if (localStorage.getItem(APP_HAS_LOGGED_IN_ONCE_KEY) === '1') {
            setHasLoggedInBefore(true);
        }
    }, [loginDeviceState]);

    // Ocultar mensajes de pre-login tan pronto el flujo de autenticación avanza
    useEffect(() => {
        if (loginDeviceState !== 'idle') {
            setSuccessMessage(null);
        }
    }, [loginDeviceState]);

    // Sprint 5: Redirigir a verificación si la cuenta no está verificada
    useEffect(() => {
        if (showAccountNotVerified && pendingCredentials?.email) {
            // Guardar email y redirigir a verificación
            savePendingEmail(pendingCredentials.email);
            const timer = setTimeout(() => {
                navigate('/verify-account', {
                    state: { email: pendingCredentials.email },
                });
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showAccountNotVerified, pendingCredentials, navigate]);

    // Limpiar estado al desmontar
    useEffect(() => {
        return () => {
            resetLoginState();
        };
    }, [resetLoginState]);

    // Validación local
    const validateForm = useCallback((): boolean => {
        const errors: Record<string, string> = {};

        if (!email.trim()) {
            errors.email = 'El correo electrónico es obligatorio';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = 'Ingresa un correo electrónico válido';
        }

        if (!password) {
            errors.password = 'La contraseña es obligatoria';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [email, password]);

    // Submit handler
    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        clearError();
        setFieldErrors({});
        setSuccessMessage(null);

        if (!validateForm()) return;

        await login({ email, password });

        // El useEffect de loginDeviceState maneja la navegación
        // y el modal de mismatch

        // Mapear errores del servidor a campos si hay error
        const serverError = useAuthStore.getState().error as ApiError | null;
        if (serverError?.errors) {
            setFieldErrors(normalizeLoginFieldErrors(serverError.errors));
        }
    }, [email, password, login, validateForm, clearError]);

    // Handler para completar el bind
    const handleCompleteBind = useCallback(async () => {
        await completeBind();
        // El useEffect de loginDeviceState maneja la navegación
    }, [completeBind]);

    // Handler para cerrar el modal de mismatch
    // Solo llama a resetLoginState ya que showMismatchModal se deriva del estado
    const handleCloseMismatchModal = useCallback(() => {
        resetLoginState();
    }, [resetLoginState]);

    const handleSkipBiometricSetup = useCallback(() => {
        setShowBiometricPrompt(false);
        setBiometricSetupPayload(null);
        navigateAfterLogin();
    }, [navigateAfterLogin]);

    const handleConfirmBiometricSetup = useCallback(async () => {
        if (!biometricSetupPayload || isBiometricSetupLoading) {
            return;
        }

        setIsBiometricSetupLoading(true);

        try {
            const authenticated = await biometricService.authenticate();
            if (authenticated) {
                await enableBiometric(biometricSetupPayload.email, biometricSetupPayload.token);
            }
        } catch (biometricError) {
            console.error('[Login] Error enabling biometric after login:', biometricError);
        } finally {
            setIsBiometricSetupLoading(false);
            setShowBiometricPrompt(false);
            setBiometricSetupPayload(null);
            navigateAfterLogin();
        }
    }, [biometricSetupPayload, isBiometricSetupLoading, enableBiometric, navigateAfterLogin]);

    // Determinar si mostrar UI de bind required
    const showBindRequired = loginDeviceState === 'bind_required';
    const isBinding = loginDeviceState === 'binding';
    const isNativePlatform = Capacitor.isNativePlatform();
    const showBiometricOnlyLogin =
        isNativePlatform &&
        biometricInitialized &&
        biometricAvailable &&
        biometricEnabled &&
        hasCredentials;
    const waitBiometricInitialization = isNativePlatform && !biometricInitialized;
    const showLoginHints = !waitBiometricInitialization && !showBiometricOnlyLogin && loginDeviceState === 'idle';
    const showInvitationFirstAccessHint = showLoginHints && !hasLoggedInBefore && !biometricEnabled;

    // Sprint 7: Handler para login biométrico exitoso
    const handleBiometricLogin = useCallback(async (_storedEmail: string, storedToken: string) => {
        // Forzar selección de modo en cada inicio de sesión, incluido biométrico.
        localStorage.removeItem(MARKETPLACE_MODE_ONBOARDED_KEY);

        // Restaurar token en localStorage y verificar con el servidor
        localStorage.setItem(TOKEN_STORAGE_KEY, storedToken);
        useAuthStore.setState({ token: storedToken });

        try {
            await useAuthStore.getState().checkAuth();
            const user = useAuthStore.getState().user;
            if (user) {
                // Token válido, navegar
                navigate(user.is_admin ? '/admin' : '/');
            } else {
                // Token expirado, limpiar biométrico
                useBiometricStore.getState().disableBiometric();
                localStorage.removeItem(TOKEN_STORAGE_KEY);
            }
        } catch {
            // Token inválido
            useBiometricStore.getState().disableBiometric();
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
    }, [navigate]);

    const handleSocialLogin = useCallback((provider: SocialProvider) => {
        const providerName = provider === 'google' ? 'Google' : 'Facebook';
        setSocialAuthMessage(`El inicio de sesión con ${providerName} estará disponible cuando terminemos de configurar la conexión segura.`);
    }, []);


    return (
        <div
            className="flex min-h-dvh flex-col items-center justify-center bg-background"
            style={{ padding: 'var(--spacing-safe-area)' }}
        >
            <div className="flex w-full max-w-[400px] flex-1 flex-col justify-center px-8 py-8">
                {/* ── Logo & App Name ───────────────────────── */}
                <header className="mb-10 flex flex-col items-center text-center">
                    <img src="/logo3png.png" alt="Troquero" className="mb-4 h-16 w-16 object-contain" />
                    <h1 className="text-2xl font-bold tracking-tight text-text">
                        Troquero<span className="text-brand-red">.</span>mx
                    </h1>
                    <p className="mt-1 text-sm text-text-muted">Encuentra carga en segundos.</p>
                </header>

                {showLoginHints && successMessage && !error && (
                    <div className="mb-5 rounded-xl border border-success/30 bg-success/8 p-3 text-xs text-success">{successMessage}</div>
                )}

                {showInvitationFirstAccessHint && (
                    <div className="mb-5 rounded-xl border border-primary/30 bg-primary/8 p-3 text-xs text-primary">
                        ¿Primer acceso por invitación? Usa tu correo invitado y,{' '}
                        <Link to="/forgot-password" className="font-semibold underline">recupera tu contraseña aquí</Link>.
                    </div>
                )}

                {showAccountNotVerified && (
                    <div className="mb-5 rounded-xl border border-warning/30 bg-warning/8 p-3 text-xs text-warning">
                        Tu cuenta no está verificada. Redirigiendo...
                    </div>
                )}

                {error && !error.errors && loginDeviceState !== 'device_mismatch' && !showAccountNotVerified && (
                    <div className="mb-5 rounded-xl border border-error/30 bg-error/8 p-3 text-xs text-error">{error.message}</div>
                )}

                {!showBindRequired && waitBiometricInitialization && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-text-muted">
                        <Spinner size="default" />
                        <p className="text-sm">Preparando acceso seguro...</p>
                    </div>
                )}

                {!showBindRequired && !waitBiometricInitialization && showBiometricOnlyLogin && (
                    <div className="flex flex-1 flex-col items-center justify-start pt-4">
                        <BiometricLoginButton onAuthenticated={handleBiometricLogin} showDivider={false} />
                    </div>
                )}

                {!showBindRequired && !waitBiometricInitialization && !showBiometricOnlyLogin && (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
                        <Input
                            label="Correo electrónico"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={fieldErrors.email}
                            placeholder="tu@email.com"
                            autoComplete="email"
                            autoCapitalize="none"
                            inputMode="email"
                            disabled={isLoading}
                        />

                        <Input
                            label="Contraseña"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={fieldErrors.password}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            disabled={isLoading}
                        />

                        <Link
                            to="/forgot-password"
                            className="self-end text-sm font-medium text-primary"
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>

                        <Button type="submit" variant="primary" size="large" fullWidth isLoading={isLoading}>
                            Iniciar sesión
                        </Button>

                        <div className="flex items-center gap-3" aria-hidden="true">
                            <span className="h-px flex-1 bg-border" />
                            <span className="text-sm text-text-muted">o</span>
                            <span className="h-px flex-1 bg-border" />
                        </div>

                        <div className="flex flex-col gap-3">
                            <SocialAuthButton provider="google" onClick={() => handleSocialLogin('google')} />
                            <SocialAuthButton provider="facebook" onClick={() => handleSocialLogin('facebook')} />
                        </div>

                        {socialAuthMessage && (
                            <p className="text-center text-xs text-text-muted" role="status">{socialAuthMessage}</p>
                        )}

                        {Capacitor.isNativePlatform() && biometricAvailable && !biometricEnabled && (
                            <label className="flex min-h-12 cursor-pointer items-center justify-center gap-3 rounded-xl border border-border bg-surface px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={enableBiometricOnLogin}
                                    onChange={(e) => setEnableBiometricOnLogin(e.target.checked)}
                                    disabled={isLoading}
                                    className="h-5 w-5 shrink-0 cursor-pointer accent-primary"
                                />
                                <span className="text-sm text-text-secondary">
                                    Activar inicio con {biometryType ? getBiometryName(biometryType) : 'huella'}
                                </span>
                            </label>
                        )}

                        {/* ── Divider + biometric ─────────────── */}
                        <BiometricLoginButton onAuthenticated={handleBiometricLogin} />

                        <p className="mt-4 text-center text-sm text-text-muted">
                            ¿No tienes cuenta?{' '}
                            <Link to="/register" className="font-semibold text-primary">Regístrate</Link>
                        </p>
                    </form>
                )}

                {showBindRequired && (
                    <div className="flex flex-1 flex-col">
                        <div className="mb-6 rounded-xl border border-info/20 bg-info-bg p-4">
                            <p className="text-sm text-text-secondary">
                                Es la primera vez que ingresas desde este dispositivo.
                                ¿Deseas vincularlo a tu cuenta?
                            </p>
                            <p className="mt-2 text-xs text-text-muted">
                                Solo podrás acceder desde este dispositivo por seguridad.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Button type="button" variant="primary" size="large" fullWidth isLoading={isBinding} onClick={handleCompleteBind}>
                                Vincular este dispositivo
                            </Button>
                            <Button type="button" variant="secondary" size="large" fullWidth disabled={isBinding} onClick={resetLoginState}>
                                Cancelar
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <DeviceMismatchModal isOpen={showMismatchModal} onClose={handleCloseMismatchModal} email={email || pendingCredentials?.email} />
            <DeviceAlreadyBoundModal isOpen={showAlreadyBoundModal} onClose={handleCloseMismatchModal} />
            <BiometricPrompt isOpen={showBiometricPrompt} biometryType={biometryType} onConfirm={handleConfirmBiometricSetup} onSkip={handleSkipBiometricSetup} isLoading={isBiometricSetupLoading} />
        </div>
    );
}
